// src/components/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
    userService, 
    siteService, 
    suggestionService, 
    proposalService, 
    mapService,
    teamService,
    eventsService,
    newsService,
    associationService 
} from '../services/api';

// --- Importación de subcomponentes ---
import AdminUsersTab from './dashboard/AdminUsersTab';
import AdminSiteConfigTab from './dashboard/AdminSiteConfigTab';
import AdminChallengesTab from './dashboard/AdminChallengesTab'; 
import AdminTeamTab from './dashboard/AdminTeamTab';          // NUEVO
import AdminDistrictsTab from './dashboard/AdminDistrictsTab';// NUEVO
import AdminNewsTab from './dashboard/AdminNewsTab';          // NUEVO
import AdminEventsTab from './dashboard/AdminEventsTab';      // NUEVO
import AdminAssociationsTab from './dashboard/AdminAssociationsTab'; // NUEVO
import AdminSuggestionsTab from './dashboard/AdminSuggestionsTab'; // NUEVO
import AdminProposalsTab from './dashboard/AdminProposalsTab'; // NUEVO

// --- COMPONENTE SmallBar (Mantenido) ---
function SmallBar({label, value, max}){
  const pct = Math.round((value/max)*100 || 0);
  return (
    <div className='mb-3'>
      <div className='text-sm text-gray-600'>{label} <span className='font-semibold'>{value}</span></div>
      <div className='w-full bg-gray-200 h-3 rounded mt-1'><div style={{width:`${pct}%`}} className='h-3 rounded bg-[color:var(--primary)]' /></div>
    </div>
  );
}

// --- COMPONENTE Dashboard ---
export default function Dashboard({ isAdmin }){
  const [activeTab, setActiveTab] = useState('stats');
  
  // Estados para datos específicos
  const [users, setUsers] = useState([]);
  const [site, setSite] = useState(null);
  const [districts, setDistricts] = useState([]); // Añadido
  const [team, setTeam] = useState([]);           // Añadido
  const [events, setEvents] = useState([]);         // Añadido
  const [news, setNews] = useState([]);           // Añadido
  const [associations, setAssociations] = useState([]); // Añadido
  const [suggestions, setSuggestions] = useState([]); // Añadido
  const [proposals, setProposals] = useState([]);     // Añadido

  const [statsData, setStatsData] = useState({ 
    suggestions: [],
    proposals: [],
    districts: []
  });

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false); 
  const [error, setError] = useState(null);

  // Mapeo de pestañas que requieren cargar datos (inicialmente o en cada cambio de tab)
  const tabsThatNeedData = ['stats', 'users', 'site', 'districts', 'team', 'news', 'events', 'associations', 'suggestions', 'proposals'];

  useEffect(() => {
    // Solo carga datos si la pestaña actual lo requiere y, si es administrativa, verifica isAdmin
    if (tabsThatNeedData.includes(activeTab) && (activeTab === 'stats' || isAdmin)) {
      loadData();
    }
    // NOTA: 'challenges' carga sus propios datos dentro de AdminChallengesTab
  }, [activeTab, isAdmin]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'stats':
            // Las estadísticas cargan las listas necesarias para el cálculo (suggestions, proposals, districts)
            const [suggestionsRes, proposalsRes, districtsRes] = await Promise.all([
                suggestionService.getSuggestions(),
                proposalService.getProposals(),
                mapService.getDistricts()
            ]);

            setStatsData({
                suggestions: suggestionsRes.data || suggestionsRes,
                proposals: proposalsRes.data || proposalsRes,
                districts: districtsRes.data || districtsRes,
            });
            break;

        case 'users':
          const userResponse = await userService.getUsers(); 
          setUsers(userResponse.data || userResponse); 
          break;

        case 'site':
          const siteResponse = await siteService.getSite();
          setSite(siteResponse.data || siteResponse); 
          break;

        case 'districts':
            const districtsResponse = await mapService.getDistricts();
            setDistricts(districtsResponse.data || districtsResponse);
            break;

        case 'team':
            const teamResponse = await teamService.getTeam();
            setTeam(teamResponse.data || teamResponse);
            break;
        
        case 'events':
            const eventsResponse = await eventsService.getEvents();
            setEvents(eventsResponse.data || eventsResponse);
            break;

        case 'news':
            const newsResponse = await newsService.getNews();
            setNews(newsResponse.data || newsResponse);
            break;

        case 'associations':
            const associationsResponse = await associationService.getAssociations();
            setAssociations(associationsResponse.data || associationsResponse);
            break;

        case 'suggestions':
            // Sugerencias requieren lectura para Admin
            const suggestionsListRes = await suggestionService.getSuggestions();
            setSuggestions(suggestionsListRes.data || suggestionsListRes);
            break;

        case 'proposals':
            // Propuestas requieren lectura para Admin
            const proposalsListRes = await proposalService.getProposals();
            setProposals(proposalsListRes.data || proposalsListRes);
            break;

        default:
          break;
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Error al cargar los datos. Verifique la conexión con el API.');
    } finally {
      setLoading(false);
    }
  };

  // --- RENDERIZADOR DE ESTADÍSTICAS (MANTENIDO) ---
  const renderStats = () => {
    const { suggestions, proposals, districts } = statsData;

    const totalSuggestions = (suggestions || []).length;
    const totalVotes = (proposals || []).reduce((s,p)=>s+p.votes,0);
    const maxPart = Math.max(...(districts || []).map(d=>d.participants),1);

    return (
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div className='bg-white p-4 rounded shadow'>
          <h4 className='font-semibold mb-2'>Sugerencias</h4>
          <SmallBar label='Total de Sugerencias' value={totalSuggestions} max={50} />
          {/* <SmallBar label='Votos totales en Propuestas' value={totalVotes} max={200} /> */}
        </div>
        <div className='bg-white p-4 rounded shadow'>
          <h4 className='font-semibold mb-2'>Participación por distrito</h4>
          {(districts || []).map(d=> <SmallBar key={d.id} label={d.name} value={d.participants} max={maxPart} />)}
        </div>
      </div>
    );
  };

  // --- RENDERIZADO PRINCIPAL (PÚBLICO Y ADMIN) ---

  // Si no es admin, solo muestra la vista pública (que depende de statsData)
  if(!isAdmin) return (
    <section className='max-w-6xl mx-auto p-6'>
      <h3 className='text-xl font-semibold mb-4'>Panel público</h3>
       {loading ? (
        <div className="text-center py-6 text-[color:var(--primary)]">Cargando datos públicos...</div>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div className='bg-white p-4 rounded shadow'>Sugerencias: <div className='text-2xl font-bold'>{(statsData.suggestions||[]).length}</div></div>
          <div className='bg-white p-4 rounded shadow'>Votos totales: <div className='text-2xl font-bold'>{(statsData.proposals||[]).reduce((s,p)=>s+p.votes,0)}</div></div>
          <div className='bg-white p-4 rounded shadow'>Distritos activos: <div className='text-2xl font-bold'>{(statsData.districts||[]).length}</div></div>
        </div>
      )}
    </section>
  );

  // Si es admin, muestra el dashboard completo
  return (
    <section className='max-w-6xl mx-auto p-6'>
      <h3 className='text-xl font-semibold mb-4'>Dashboard ADMIN</h3>
      
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Navegación de Pestañas */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex flex-wrap space-x-4 md:space-x-8">
          {[
            // { key: 'stats', label: 'Estadísticas' }, 
            { key: 'users', label: 'Usuarios' }, 
            { key: 'site', label: 'Config. Sitio' },
            { key: 'districts', label: 'Distritos' },
            { key: 'team', label: 'Equipo' },
            { key: 'news', label: 'Noticias' },
            { key: 'events', label: 'Eventos' },
            { key: 'associations', label: 'Asociaciones' },
            // { key: 'challenges', label: 'Desafíos' },
            { key: 'proposals', label: 'Propuestas' },
            { key: 'suggestions', label: 'Sugerencias' }
          ].map(({ key, label }) => (
             <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`${
                  activeTab === key
                    ? 'border-[color:var(--primary)] text-[color:var(--primary)]'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
             >
                {label}
            </button>
          ))}
        </nav>
      </div>

      {loading && tabsThatNeedData.includes(activeTab) ? (
        <div className="text-center py-12 text-[color:var(--primary)]">Cargando datos de {activeTab === 'users' ? 'usuarios' : activeTab}...</div>
      ) : (
        <>
          {/* Contenido de las Pestañas */}
          {activeTab === 'stats' && renderStats()}
          
          {activeTab === 'users' && <AdminUsersTab 
                                        users={users} 
                                        setUsers={setUsers} 
                                        actionLoading={actionLoading} 
                                        setActionLoading={setActionLoading} 
                                        setError={setError}
                                    />}
          
          {activeTab === 'site' && <AdminSiteConfigTab 
                                        site={site} 
                                        setSite={setSite} 
                                        actionLoading={actionLoading} 
                                        setActionLoading={setActionLoading} 
                                        setError={setError}
                                    />}

          {activeTab === 'districts' && <AdminDistrictsTab 
                                            data={districts}
                                            setData={setDistricts}
                                            actionLoading={actionLoading} 
                                            setActionLoading={setActionLoading} 
                                            setError={setError}
                                        />}

          {activeTab === 'team' && <AdminTeamTab 
                                            data={team}
                                            setData={setTeam}
                                            actionLoading={actionLoading} 
                                            setActionLoading={setActionLoading} 
                                            setError={setError}
                                        />}
                                        
          {activeTab === 'news' && <AdminNewsTab 
                                            data={news}
                                            setData={setNews}
                                            actionLoading={actionLoading} 
                                            setActionLoading={setActionLoading} 
                                            setError={setError}
                                        />}
                                        
          {activeTab === 'events' && <AdminEventsTab 
                                            data={events}
                                            setData={setEvents}
                                            actionLoading={actionLoading} 
                                            setActionLoading={setActionLoading} 
                                            setError={setError}
                                        />}
                                        
          {activeTab === 'associations' && <AdminAssociationsTab 
                                            data={associations}
                                            setData={setAssociations}
                                            actionLoading={actionLoading} 
                                            setActionLoading={setActionLoading} 
                                            setError={setError}
                                        />}

          {activeTab === 'suggestions' && <AdminSuggestionsTab 
                                            data={suggestions}
                                            setData={setSuggestions}
                                            actionLoading={actionLoading} 
                                            setActionLoading={setActionLoading} 
                                            setError={setError}
                                        />}
            
          {activeTab === 'proposals' && <AdminProposalsTab 
                                            data={proposals}
                                            setData={setProposals}
                                            actionLoading={actionLoading} 
                                            setActionLoading={setActionLoading} 
                                            setError={setError}
                                            districts={statsData.districts} // Pasar distritos para filtros/dropdowns
                                        />}
            
          {activeTab === 'challenges' && <AdminChallengesTab
                                            actionLoading={actionLoading} 
                                            setActionLoading={setActionLoading} 
                                            setError={setError}
                                        />}
        </>
      )}
    </section>
  );
}